import React, { useEffect, useMemo, useState, useRef } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { moduleCanDo } from "../lib/modules";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Download, Filter as FilterIcon, X, History, Building2, ClipboardList, AlertTriangle, Users, PhoneCall, PhoneOff, TrendingUp, TrendingDown, Trophy, Crown, Award, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ClientFormDialog from "../components/ClientFormDialog";
import { CrmStageBadge, CrmStageButtons, CrmStageFilterChips, deriveStage, STAGE_KEYS } from "../components/CrmStage";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem,
} from "../components/ui/dropdown-menu";

const CLIENT_TYPES_FOR_SAMPLE = ["ATL 1.0", "ATL 2.0", "Private School", "PM-SHRI JNV", "PM-SHRI KV", "CSR", "College", "Other"];

const hasContactData = (c) => (Array.isArray(c.contacts) ? c.contacts : []).some(
  (x) => x && [x.name, x.phone, x.email].some((v) => String(v ?? "").trim()),
);

export default function CRMClients() {
  const { user } = useAuth();
  const isAdmin = user?.role === "master_admin" || moduleCanDo(user, "crm_clients", "edit");
  const isMaster = user?.role === "master_admin";
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyClient, setHistoryClient] = useState(null);
  const [historyLogs, setHistoryLogs] = useState([]);

  const openHistory = async (c) => {
    setHistoryClient(c);
    setHistoryLogs([]);
    try {
      const { data } = await api.get(`/clients/${c.id}/change-log`);
      setHistoryLogs(data.logs || []);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const openLogs = async (c) => {
    setLogsClient(c);
    setLogsList([]);
    setLogsLoading(true);
    try {
      const { data } = await api.get(`/crm/clients/${c.id}/reports`);
      setLogsList(data || []);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLogsLoading(false); }
  };
  const fileRef = useRef(null);
  const [filter, setFilter] = useState("all"); // all | pending | approved
  const [stageFilter, setStageFilter] = useState("all");
  const [udiseFilter, setUdiseFilter] = useState("all"); // all | invalid | duplicate | missing | issues
  const [logsClient, setLogsClient] = useState(null);
  const [logsList, setLogsList] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  // Multi-select facet filters (Set of selected values, empty = no restriction)
  const [typeFilters, setTypeFilters] = useState(() => new Set());
  const [locationFilters, setLocationFilters] = useState(() => new Set());
  const [assignedFilters, setAssignedFilters] = useState(() => new Set());
  // v109.4 — dedicated state / district selectors (State drives District options)
  const [stateFilter, setStateFilter] = useState("all");
  const [districtFilter, setDistrictFilter] = useState("all");
  // v109.4 — dedicated Contact-status filter (all | contacted | none)
  const [contactFilter, setContactFilter] = useState("all");
  // v109.6 — Follow-up / No-Log filter (all | today | overdue | upcoming | nolog)
  const [followupFilter, setFollowupFilter] = useState("all");
  // v109.4 — Recycle Bin dialog + rows
  const [recycleOpen, setRecycleOpen] = useState(false);
  const [recycleRows, setRecycleRows] = useState([]);
  const [recycleLoading, setRecycleLoading] = useState(false);
  // v109.5 — Module Access (Master Admin only)
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessData, setAccessData] = useState({ access_user_ids: [], all_employees: [] });
  const [accessSaving, setAccessSaving] = useState(false);

  // Bulk selection state — admins can multi-select rows + reassign in one shot.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  // v109.9.168 — Bulk-Update-State + Merge Clients dialogs
  const [bulkStateOpen, setBulkStateOpen] = useState(false);
  const [bulkState, setBulkState] = useState("");
  const [bulkStateBusy, setBulkStateBusy] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeMainId, setMergeMainId] = useState("");
  const [mergeFieldMap, setMergeFieldMap] = useState({});
  const [mergeBusy, setMergeBusy] = useState(false);
  const [bulkMode, setBulkMode] = useState("selection"); // "selection" | "transfer"
  const [bulkUsers, setBulkUsers] = useState([]);
  // v109 — Card / List view synced with the top-bar toggle
  const [crmViewMode] = useGlobalViewMode();
  const [bulkTargetId, setBulkTargetId] = useState("");
  const [bulkFromUserId, setBulkFromUserId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadBulkUsers = async () => {
    try {
      const { data } = await api.get("/crm/sales-eligible-users");
      setBulkUsers(data || []);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const openBulkSelection = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one client first");
      return;
    }
    setBulkMode("selection");
    setBulkTargetId("");
    setBulkFromUserId("");
    await loadBulkUsers();
    setBulkOpen(true);
  };
  const openBulkTransfer = async () => {
    setBulkMode("transfer");
    setBulkTargetId("");
    setBulkFromUserId("");
    await loadBulkUsers();
    setBulkOpen(true);
  };

  const submitBulkAssign = async () => {
    if (!bulkTargetId) { toast.error("Pick the new owner"); return; }
    if (bulkMode === "transfer" && !bulkFromUserId) {
      toast.error("Pick the current owner whose clients you want to transfer");
      return;
    }
    if (bulkMode === "transfer" && bulkFromUserId === bulkTargetId) {
      toast.error("Current and new owner cannot be the same");
      return;
    }
    setBulkBusy(true);
    try {
      const body = bulkMode === "transfer"
        ? { user_id: bulkTargetId, from_user_id: bulkFromUserId }
        : { user_id: bulkTargetId, client_ids: Array.from(selected) };
      const { data } = await api.post("/crm/clients/bulk-assign-sales", body);
      toast.success(
        `Reassigned ${data.updated} client(s)` +
        (data.skipped_already_assigned ? ` (${data.skipped_already_assigned} already assigned, skipped)` : "")
      );
      setBulkOpen(false);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const [monthLeaders, setMonthLeaders] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/records/crm_clients");
      setClients(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    api.get("/crm/clients/month-leaders").then((r) => setMonthLeaders(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  // Build option lists from current data. We include explicit "Not Filled" sentinel
  // when at least one record has the value blank — so users can filter for empties.
  const TYPE_NOT_FILLED = "__NOT_FILLED__";
  const LOCATION_NOT_FILLED = "__NOT_FILLED__";

  const typeOptions = useMemo(() => {
    const present = new Set();
    let hasBlank = false;
    clients.forEach((c) => {
      const v = (c.client_type || "").trim();
      if (v) present.add(v); else hasBlank = true;
    });
    const arr = Array.from(present).sort();
    if (hasBlank) arr.push(TYPE_NOT_FILLED);
    return arr;
  }, [clients]);
  const locationOptions = useMemo(() => {
    const present = new Set();
    let hasBlank = false;
    clients.forEach((c) => {
      const state = (c.state || "").trim();
      const district = (c.district || "").trim();
      if (state || district) present.add(`${state}||${district}`);
      else hasBlank = true;
    });
    const arr = Array.from(present).sort();
    if (hasBlank) arr.push(LOCATION_NOT_FILLED);
    return arr;
  }, [clients]);
  const assignedOptions = useMemo(() => {
    const arr = clients.map((c) => c.assigned_to_name || "__UNASSIGNED__");
    return Array.from(new Set(arr)).sort();
  }, [clients]);

  // v109.4 — State and District options (District list is scoped to selected State)
  const stateOptions = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => { const s = (c.state || "").trim(); if (s) set.add(s); });
    return Array.from(set).sort();
  }, [clients]);
  const districtOptions = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => {
      const s = (c.state || "").trim();
      const d = (c.district || "").trim();
      if (!d) return;
      if (stateFilter !== "all" && s !== stateFilter) return;
      set.add(d);
    });
    return Array.from(set).sort();
  }, [clients, stateFilter]);

  const toggleSet = (setter) => (val) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(val)) next.delete(val); else next.add(val);
    return next;
  });

  const clearAllFilters = () => {
    setTypeFilters(new Set());
    setLocationFilters(new Set());
    setAssignedFilters(new Set());
    setStateFilter("all");
    setDistrictFilter("all");
    setContactFilter("all");
    setStageFilter("all");
    setUdiseFilter("all");
    setFollowupFilter("all");
  };

  // v109.6 — Follow-up helper: today ISO for date compare
  const _todayIso = () => new Date().toISOString().slice(0, 10);
  const _matchFollowup = (c, key) => {
    const l = c.last_log;
    if (key === "nolog") return !l || !l.by_name;
    const nf = (l && l.next_follow_up) || "";
    if (!nf) return false;
    const t = _todayIso();
    if (key === "today") return nf === t;
    if (key === "overdue") return nf < t;
    if (key === "upcoming") return nf > t;
    return true;
  };

  const noContactCount = useMemo(() => clients.filter((c) => !hasContactData(c)).length, [clients]);
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return clients.filter((c) => {
      if (filter === "pending" && c.approval_status === "Approved") return false;
      if (filter === "approved" && c.approval_status !== "Approved") return false;
      if (filter === "no_contact" && hasContactData(c)) return false;
      if (stageFilter !== "all" && (c.crm_stage || deriveStage(c)) !== stageFilter) return false;
      if (udiseFilter !== "all") {
        const flags = c.udise_flags || [];
        if (udiseFilter === "issues" && flags.length === 0) return false;
        if (udiseFilter === "invalid" && !flags.includes("Invalid")) return false;
        if (udiseFilter === "duplicate" && !flags.includes("Duplicate")) return false;
        if (udiseFilter === "missing" && !flags.includes("Missing")) return false;
      }
      // v109.4 — Contact status filter
      if (contactFilter !== "all") {
        const list = Array.isArray(c.contacts) ? c.contacts : [];
        const hasAny = list.some((x) => x && ((x.phone || "").trim() || (x.email || "").trim() || (x.name || "").trim()));
        if (contactFilter === "contacted" && !hasAny) return false;
        if (contactFilter === "none" && hasAny) return false;
      }
      // v109.4 — State + District filters (District is scoped to selected State)
      if (stateFilter !== "all" && (c.state || "").trim() !== stateFilter) return false;
      if (districtFilter !== "all" && (c.district || "").trim() !== districtFilter) return false;
      // v109.6 — Follow-up / No-Log filter
      if (followupFilter !== "all" && !_matchFollowup(c, followupFilter)) return false;
      if (typeFilters.size > 0) {
        const t = (c.client_type || "").trim();
        const matchVal = t || TYPE_NOT_FILLED;
        if (!typeFilters.has(matchVal)) return false;
      }
      if (locationFilters.size > 0) {
        const state = (c.state || "").trim();
        const district = (c.district || "").trim();
        const key = (state || district) ? `${state}||${district}` : LOCATION_NOT_FILLED;
        if (!locationFilters.has(key)) return false;
      }
      if (assignedFilters.size > 0) {
        const key = c.assigned_to_name || "__UNASSIGNED__";
        if (!assignedFilters.has(key)) return false;
      }
      return !q || [c.client_name, c.u_dise, c.client_type, c.district, c.state, c.assigned_to_name]
        .some((v) => String(v ?? "").toLowerCase().includes(q));
    });
  }, [clients, search, filter, stageFilter, udiseFilter, contactFilter, stateFilter, districtFilter, followupFilter, typeFilters, locationFilters, assignedFilters]);

  // Counts for U-DISE issue filter chips
  // Cross-filtered counts helper: excludes ONE dimension from the active filter set.
  const _crossCount = (excludeDim, predicate) => {
    let n = 0;
    clients.forEach((c) => {
      if (filter === "pending" && c.approval_status === "Approved") return;
      if (filter === "approved" && c.approval_status !== "Approved") return;
      if (filter === "no_contact" && hasContactData(c)) return;
      if (excludeDim !== "stage" && stageFilter !== "all" && (c.crm_stage || deriveStage(c)) !== stageFilter) return;
      if (excludeDim !== "udise" && udiseFilter !== "all") {
        const flags = c.udise_flags || [];
        if (udiseFilter === "issues" && flags.length === 0) return;
        if (udiseFilter === "invalid" && !flags.includes("Invalid")) return;
        if (udiseFilter === "duplicate" && !flags.includes("Duplicate")) return;
        if (udiseFilter === "missing" && !flags.includes("Missing")) return;
      }
      if (excludeDim !== "contact" && contactFilter !== "all") {
        const list = Array.isArray(c.contacts) ? c.contacts : [];
        const hasAny = list.some((x) => x && ((x.phone || "").trim() || (x.email || "").trim() || (x.name || "").trim()));
        if (contactFilter === "contacted" && !hasAny) return;
        if (contactFilter === "none" && hasAny) return;
      }
      if (excludeDim !== "region") {
        if (stateFilter !== "all" && (c.state || "").trim() !== stateFilter) return;
        if (districtFilter !== "all" && (c.district || "").trim() !== districtFilter) return;
      }
      if (excludeDim !== "followup" && followupFilter !== "all" && !_matchFollowup(c, followupFilter)) return;
      // v109.7 — Type & Assigned facets participate in cross-filter counting
      if (excludeDim !== "type" && typeFilters.size > 0) {
        const t = (c.client_type || "").trim();
        const matchVal = t || TYPE_NOT_FILLED;
        if (!typeFilters.has(matchVal)) return;
      }
      if (excludeDim !== "assigned" && assignedFilters.size > 0) {
        const key = c.assigned_to_name || "__UNASSIGNED__";
        if (!assignedFilters.has(key)) return;
      }
      if (predicate(c)) n++;
    });
    return n;
  };

  // v109.5 — UDISE counts respect Stage + Contact + Region active filters
  const udiseCounts = useMemo(() => ({
    all:       _crossCount("udise", () => true),
    issues:    _crossCount("udise", (x) => (x.udise_flags || []).length > 0),
    invalid:   _crossCount("udise", (x) => (x.udise_flags || []).includes("Invalid")),
    duplicate: _crossCount("udise", (x) => (x.udise_flags || []).includes("Duplicate")),
    missing:   _crossCount("udise", (x) => (x.udise_flags || []).includes("Missing")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [clients, filter, stageFilter, contactFilter, stateFilter, districtFilter, followupFilter, typeFilters, assignedFilters]);

  // v109.5 — Contact counts respect Stage + UDISE + Region active filters
  const contactCounts = useMemo(() => {
    const hasContact = (x) => (Array.isArray(x.contacts) ? x.contacts : []).some((c) => c && ((c.phone || "").trim() || (c.email || "").trim() || (c.name || "").trim()));
    return {
      all:       _crossCount("contact", () => true),
      contacted: _crossCount("contact", hasContact),
      none:      _crossCount("contact", (x) => !hasContact(x)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, filter, stageFilter, udiseFilter, stateFilter, districtFilter, followupFilter, typeFilters, assignedFilters]);

  // v109.6 — Follow-up counts respect every OTHER active filter
  const followupCounts = useMemo(() => ({
    all:      _crossCount("followup", () => true),
    today:    _crossCount("followup", (x) => _matchFollowup(x, "today")),
    overdue:  _crossCount("followup", (x) => _matchFollowup(x, "overdue")),
    upcoming: _crossCount("followup", (x) => _matchFollowup(x, "upcoming")),
    nolog:    _crossCount("followup", (x) => _matchFollowup(x, "nolog")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [clients, filter, stageFilter, udiseFilter, contactFilter, stateFilter, districtFilter, typeFilters, assignedFilters]);

  // Counts for the stage filter chips — v109.5 cross-filter: counts respect the OTHER active filters
  const stageCounts = useMemo(() => {
    const others = clients.filter((c) => {
      if (filter === "pending" && c.approval_status === "Approved") return false;
      if (filter === "approved" && c.approval_status !== "Approved") return false;
      if (filter === "no_contact" && hasContactData(c)) return false;
      if (udiseFilter !== "all") {
        const flags = c.udise_flags || [];
        if (udiseFilter === "issues" && flags.length === 0) return false;
        if (udiseFilter === "invalid" && !flags.includes("Invalid")) return false;
        if (udiseFilter === "duplicate" && !flags.includes("Duplicate")) return false;
        if (udiseFilter === "missing" && !flags.includes("Missing")) return false;
      }
      if (contactFilter !== "all") {
        const list = Array.isArray(c.contacts) ? c.contacts : [];
        const hasAny = list.some((x) => x && ((x.phone || "").trim() || (x.email || "").trim() || (x.name || "").trim()));
        if (contactFilter === "contacted" && !hasAny) return false;
        if (contactFilter === "none" && hasAny) return false;
      }
      if (stateFilter !== "all" && (c.state || "").trim() !== stateFilter) return false;
      if (districtFilter !== "all" && (c.district || "").trim() !== districtFilter) return false;
      if (followupFilter !== "all" && !_matchFollowup(c, followupFilter)) return false;
      // v109.7 — Stage chips respect active Type & Assigned facets
      if (typeFilters.size > 0) {
        const t = (c.client_type || "").trim();
        const matchVal = t || TYPE_NOT_FILLED;
        if (!typeFilters.has(matchVal)) return false;
      }
      if (assignedFilters.size > 0) {
        const key = c.assigned_to_name || "__UNASSIGNED__";
        if (!assignedFilters.has(key)) return false;
      }
      return true;
    });
    const counts = { total: others.length };
    STAGE_KEYS.forEach((k) => { counts[k] = 0; });
    others.forEach((c) => {
      const s = c.crm_stage || deriveStage(c);
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, filter, udiseFilter, contactFilter, stateFilter, districtFilter, followupFilter, typeFilters, assignedFilters]);

  // v109.3 — Client analytics: totals + SA leaderboard
  const analytics = useMemo(() => {
    const totals = { all: clients.length, contacted: 0, non: 0, positive: 0, negative: 0, logs: 0 };
    const perSA = new Map(); // sa_name → { name, clients, contacted, positive, logs }
    const bumpSA = (c) => {
      const name = c.assigned_to_name || "Unassigned";
      if (!perSA.has(name)) perSA.set(name, { name, clients: 0, contacted: 0, positive: 0, logs: 0 });
      return perSA.get(name);
    };
    clients.forEach((c) => {
      const contactList = Array.isArray(c.contacts) ? c.contacts : [];
      const hasContact = contactList.some((x) => x && ((x.phone || "").trim() || (x.email || "").trim() || (x.name || "").trim()));
      const stage = c.crm_stage || deriveStage(c);
      if (hasContact) totals.contacted++; else totals.non++;
      if (stage === "Positive") totals.positive++;
      if (stage === "Negative") totals.negative++;
      totals.logs += (c.log_count || 0);
      const sa = bumpSA(c);
      sa.clients++;
      if (hasContact) sa.contacted++;
      if (stage === "Positive") sa.positive++;
      sa.logs += (c.log_count || 0);
    });
    const list = Array.from(perSA.values()).filter((s) => s.name !== "Unassigned");
    const top = (key) => list.slice().sort((a, b) => b[key] - a[key])[0] || null;
    return { totals, top_clients: top("clients"), top_contacted: top("contacted"), top_positive: top("positive"), top_logs: top("logs") };
  }, [clients]);

  const approveClient = async (c) => {
    try {
      await api.post(`/crm/clients/${c.id}/approve`);
      toast.success(`Approved ${c.client_name}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const rejectClient = async (c) => {
    const reason = window.prompt("Rejection reason (optional):") || "";
    try {
      await api.post(`/crm/clients/${c.id}/reject`, { reason });
      toast.success(`Rejected ${c.client_name} — moved to Recycle Bin`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // v109.4 — Recycle Bin
  const openRecycleBin = async () => {
    setRecycleOpen(true);
    setRecycleLoading(true);
    try {
      const { data } = await api.get("/crm/clients/recycle-bin");
      setRecycleRows(data || []);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setRecycleLoading(false); }
  };
  const restoreClient = async (id, name) => {
    try {
      await api.post(`/crm/clients/${id}/restore`);
      toast.success(`Restored ${name} (Pending approval)`);
      setRecycleRows((rows) => rows.filter((r) => r.id !== id));
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const purgeClient = async (id, name) => {
    if (!window.confirm(`Permanently delete ${name}? This action cannot be undone.`)) return;
    try {
      await api.delete(`/crm/clients/${id}/purge`);
      toast.success(`Permanently deleted ${name}`);
      setRecycleRows((rows) => rows.filter((r) => r.id !== id));
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // v109.5 — Module Access management
  const openModuleAccess = async () => {
    setAccessOpen(true);
    try {
      const { data } = await api.get("/crm/module-config");
      setAccessData({ access_user_ids: data.access_user_ids || [], all_employees: data.all_employees || [] });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const toggleAccessUser = async (uid) => {
    const set = new Set(accessData.access_user_ids || []);
    if (set.has(uid)) set.delete(uid); else set.add(uid);
    const next = Array.from(set);
    setAccessData((prev) => ({ ...prev, access_user_ids: next }));
    setAccessSaving(true);
    try {
      const { data } = await api.patch("/crm/module-config", { access_user_ids: next });
      setAccessData({ access_user_ids: data.access_user_ids || next, all_employees: data.all_employees || accessData.all_employees });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      openModuleAccess();
    } finally { setAccessSaving(false); }
  };

  // v109.9.181 — Export now asks for a client-type filter and the CSV
  // includes contact-detail columns (up to 3 contacts per client).
  const [exportOpen, setExportOpen] = useState(false);
  const [exportType, setExportType] = useState("all");
  const [exportBusy, setExportBusy] = useState(false);

  const exportTypes = useMemo(() => {
    const set = new Set();
    (clients || []).forEach((c) => { const t = (c.client_type || "").trim(); if (t) set.add(t); });
    return ["all", ...Array.from(set).sort()];
  }, [clients]);

  const exportCSV = async () => {
    setExportBusy(true);
    try {
      const params = exportType && exportType !== "all" ? { type: exportType } : {};
      const r = await api.get("/crm/clients/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const fname = "clients_export" + (exportType !== "all" ? "_" + exportType.replace(/[^A-Za-z0-9]/g, "_") : "") + ".csv";
      a.href = url; a.download = fname; a.click(); URL.revokeObjectURL(url);
      toast.success(`Export downloaded (${exportType === "all" ? "All clients" : exportType})`);
      setExportOpen(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setExportBusy(false); }
  };
  const importCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/crm/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.inserted}, skipped ${r.data.skipped}`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    e.target.value = "";
  };

  const downloadSample = async (type) => {
    try {
      const r = await api.get("/crm/clients/sample-csv", { params: { type }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `client_sample_${type.replace(/[^A-Za-z0-9]/g, "_")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Sample CSV for ${type} downloaded`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (rec) => { setEditing(rec); setOpen(true); };

  const remove = async (id) => {
    try {
      await api.delete(`/records/crm_clients/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-[1500px] space-y-6">
      <PageHeader
        moduleKey="crm_clients"
        tone="sky"
        icon={Building2}
        eyebrow="CRM Module"
        title="Client Database"
        subtitle="Master client list. Add contacts, track ATL tranche status and assignments."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {isMaster && (
              <>
                <input ref={fileRef} type="file" accept=".csv" onChange={importCSV} className="hidden" data-testid="crm-import-input" />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="h-10 bg-white/80 border-sky-200 text-sky-700 hover:bg-sky-50" data-testid="crm-sample-button">
                      <Download size={14} className="mr-1.5" /> Sample CSV
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Download by Client Type</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {CLIENT_TYPES_FOR_SAMPLE.map((t) => (
                      <DropdownMenuItem
                        key={t}
                        onClick={() => downloadSample(t)}
                        className="text-[12px] cursor-pointer"
                        data-testid={`crm-sample-${t.replace(/[^A-Za-z0-9]/g, "_")}`}
                      >
                        {t}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="outline" onClick={() => fileRef.current?.click()} className="h-10 bg-white/80 border-sky-200 text-sky-700 hover:bg-sky-50" data-testid="crm-import-button">
                  Upload CSV
                </Button>
                <Button variant="outline" onClick={() => { setExportType("all"); setExportOpen(true); }} className="h-10 bg-white/80 border-sky-200 text-sky-700 hover:bg-sky-50" data-testid="crm-export-button">
                  Export CSV
                </Button>
              </>
            )}
            <Button onClick={openCreate} className="bg-gradient-to-br from-sky-500 to-cyan-600 hover:from-sky-600 hover:to-cyan-700 text-white h-10 shadow-sm" data-testid="crm-add-button">
              <Plus size={16} className="mr-1.5" /> New Client
            </Button>
          </div>
        }
      />

      <ClientFormDialog
        open={open}
        onOpenChange={setOpen}
        initial={editing}
        onSaved={load}
        title={editing ? "Edit Client" : "New Client"}
        submitLabel={editing ? "Save Changes" : "Add Client"}
      />

      {/* Change-log viewer — every PATCH on this client is auditable here */}
      <Dialog open={!!historyClient} onOpenChange={(o) => !o && setHistoryClient(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="crm-history-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <History size={16} /> Change History — {historyClient?.client_name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {historyLogs.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm" data-testid="crm-history-empty">
                No changes recorded for this client yet.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="crm-history-list">
                {historyLogs.map((e, idx) => (
                  <li key={idx} className="border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-slate-50/60 dark:bg-slate-800/40">
                    <div className="flex items-start justify-between gap-2 flex-wrap mb-1.5">
                      <div className="text-[12px]">
                        <span className="font-semibold text-slate-900 dark:text-slate-100">{e.by_name || "—"}</span>
                        <span className="text-slate-500"> ({e.by_role || "—"})</span>
                        {e.source && (
                          <span className="ml-2 text-[10px] mono bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200 px-1.5 py-0.5 rounded">via {e.source}</span>
                        )}
                      </div>
                      <span className="text-[10.5px] mono text-slate-500">{e.at ? new Date(e.at).toLocaleString() : ""}</span>
                    </div>
                    <ul className="space-y-0.5 ml-1">
                      {(e.changes || []).map((d, i) => (
                        <li key={i} className="text-[12px] text-slate-700 dark:text-slate-300">
                          <b className="text-slate-900 dark:text-slate-100">{d.field}</b>:{" "}
                          <span className="text-rose-600 line-through mono">{String(d.from ?? "—")}</span>
                          {" → "}
                          <span className="text-emerald-700 dark:text-emerald-400 font-semibold mono">{String(d.to ?? "—")}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryClient(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, U-Dise, district, assignee…" className="pl-9 h-10" data-testid="crm-search" />
        </div>

        <div className="flex items-center gap-1.5 ml-auto">
          {isAdmin && (
            <>
              <FilterChip label="All" active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all" />
              <FilterChip label="Pending Approval" active={filter === "pending"} onClick={() => setFilter("pending")} testid="filter-pending" />
              <FilterChip label="Approved" active={filter === "approved"} onClick={() => setFilter("approved")} testid="filter-approved" />
            </>
          )}
          {!isAdmin && filter === "no_contact" && (
            <FilterChip label="All" active={false} onClick={() => setFilter("all")} testid="filter-all" />
          )}
          <FilterChip label={`No Contact (${noContactCount})`} active={filter === "no_contact"} onClick={() => setFilter("no_contact")} testid="filter-no-contact" />
        </div>
      </div>

      {/* v109.3 — Stats overview: 5 client totals + 4 SA leaderboards */}
      <StatsOverview analytics={analytics} monthLeaders={monthLeaders} />

      {/* v109.5 — Single-line unified filter strip: Stage · U-DISE · Contact · Region · Follow-Up · Type · Assigned */}
      {/* v109.7.2 — Realigned: uniform chip height (h-7), small text (10.5px), symmetric section spacing */}
      <div className="rounded-xl border border-slate-200 bg-white/50 px-3 py-2" data-testid="crm-filter-strip">
        <div className="flex items-center gap-x-2 gap-y-1.5 flex-wrap">
          {/* Stage */}
          <CrmStageFilterChips
            value={stageFilter}
            onChange={setStageFilter}
            counts={stageCounts}
            testidPrefix="crm-stage-chip"
          />

          <div className="w-px h-4 bg-slate-200 self-center" />

          {/* UDISE */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-slate-500 mr-1">U-DISE</span>
            {[
              { k: "all",       l: `All`,        icon: "◆", active: "bg-gradient-to-br from-sky-500 via-blue-500 to-cyan-600 text-white shadow-sm shadow-blue-500/40 ring-1 ring-blue-300", idle: "bg-white border-slate-300 text-slate-700 hover:bg-slate-50" },
              { k: "issues",    l: `Issues`,     icon: "!", active: "bg-gradient-to-br from-orange-500 to-amber-600 text-white shadow-sm ring-1 ring-orange-500", idle: "bg-white border-orange-200 text-orange-700 hover:bg-orange-50" },
              { k: "invalid",   l: `Invalid`,    icon: "✕", active: "bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-sm ring-1 ring-rose-500", idle: "bg-white border-rose-200 text-rose-700 hover:bg-rose-50" },
              { k: "duplicate", l: `Duplicate`,  icon: "≡", active: "bg-gradient-to-br from-amber-500 to-yellow-600 text-white shadow-sm ring-1 ring-amber-500", idle: "bg-white border-amber-200 text-amber-700 hover:bg-amber-50" },
              { k: "missing",   l: `Missing`,    icon: "∅", active: "bg-gradient-to-br from-slate-600 to-slate-700 text-white shadow-sm ring-1 ring-slate-600", idle: "bg-white border-slate-300 text-slate-700 hover:bg-slate-50" },
            ].map(({ k, l, icon, active, idle }) => {
              const isActive = udiseFilter === k;
              return (
                <button
                  key={k}
                  onClick={() => setUdiseFilter(k)}
                  className={`h-7 px-2.5 text-[10.5px] font-bold rounded-full border inline-flex items-center gap-1 transition-all ${isActive ? `${active} -translate-y-[1px] border-transparent` : `${idle} hover:-translate-y-[1px] hover:shadow-sm`}`}
                  data-testid={`udise-filter-chip-${k}`}
                >
                  {isActive && <span className="w-1 h-1 rounded-full bg-white/95" />}
                  <span className="text-[9px] opacity-90">{icon}</span>
                  {l} <span className="mono opacity-90">({udiseCounts[k]})</span>
                </button>
              );
            })}
          </div>

          <div className="w-px h-4 bg-slate-200 self-center" />

          {/* Contact status */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-slate-500 mr-1">Contact</span>
            {[
              { k: "all",       l: "All",             icon: "●", active: "bg-gradient-to-br from-sky-500 via-blue-500 to-cyan-600 text-white shadow-sm shadow-blue-500/40 ring-1 ring-blue-300", idle: "bg-white border-slate-300 text-slate-700 hover:bg-slate-50", count: contactCounts.all },
              { k: "contacted", l: "Have Contacts",   icon: "✓", active: "bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-sm ring-1 ring-emerald-500", idle: "bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50", count: contactCounts.contacted },
              { k: "none",      l: "No Contact",      icon: "○", active: "bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-sm ring-1 ring-rose-500", idle: "bg-white border-rose-200 text-rose-700 hover:bg-rose-50", count: contactCounts.none },
            ].map(({ k, l, icon, active, idle, count }) => {
              const isActive = contactFilter === k;
              return (
                <button
                  key={k}
                  onClick={() => setContactFilter(k)}
                  className={`h-7 px-2.5 text-[10.5px] font-bold rounded-full border inline-flex items-center gap-1 transition-all ${isActive ? `${active} -translate-y-[1px] border-transparent` : `${idle} hover:-translate-y-[1px] hover:shadow-sm`}`}
                  data-testid={`contact-filter-chip-${k}`}
                >
                  {isActive && <span className="w-1 h-1 rounded-full bg-white/95" />}
                  <span className="text-[9px] opacity-90">{icon}</span>
                  {l} <span className="mono opacity-90">({count})</span>
                </button>
              );
            })}
          </div>

          <div className="w-px h-4 bg-slate-200 self-center" />

          {/* State + District */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-slate-500 mr-1">Region</span>
            <StateDistrictPicker
              label="State"
              value={stateFilter}
              options={stateOptions}
              onChange={(v) => { setStateFilter(v); setDistrictFilter("all"); }}
              testid="crm-state-picker"
              activeCls="bg-gradient-to-br from-indigo-500 to-blue-600"
            />
            <StateDistrictPicker
              label="District"
              value={districtFilter}
              options={districtOptions}
              onChange={setDistrictFilter}
              disabled={stateFilter === "all"}
              testid="crm-district-picker"
              activeCls="bg-gradient-to-br from-sky-500 to-cyan-600"
            />
          </div>

          <div className="w-px h-4 bg-slate-200 self-center" />

          {/* v109.6 — Follow-up filters (Today / Overdue / Upcoming) + No-Log filter */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-slate-500 mr-1">Follow-Up</span>
            {[
              { k: "all",      l: "All",       icon: "●", active: "bg-gradient-to-br from-sky-500 via-blue-500 to-cyan-600 text-white shadow-sm ring-1 ring-blue-300", idle: "bg-white border-slate-300 text-slate-700 hover:bg-slate-50", count: followupCounts.all },
              { k: "today",    l: "Today",     icon: "▣", active: "bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-sm ring-1 ring-emerald-500", idle: "bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50", count: followupCounts.today },
              { k: "overdue",  l: "Overdue",   icon: "!", active: "bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-sm ring-1 ring-rose-500", idle: "bg-white border-rose-200 text-rose-700 hover:bg-rose-50", count: followupCounts.overdue },
              { k: "upcoming", l: "Upcoming",  icon: "▶", active: "bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-sm ring-1 ring-amber-500", idle: "bg-white border-amber-200 text-amber-700 hover:bg-amber-50", count: followupCounts.upcoming },
              { k: "nolog",    l: "No Log",    icon: "✕", active: "bg-gradient-to-br from-slate-600 to-slate-800 text-white shadow-sm ring-1 ring-slate-600", idle: "bg-white border-slate-300 text-slate-700 hover:bg-slate-50", count: followupCounts.nolog },
            ].map(({ k, l, icon, active, idle, count }) => {
              const isActive = followupFilter === k;
              return (
                <button
                  key={k}
                  onClick={() => setFollowupFilter(k)}
                  className={`h-7 px-2.5 text-[10.5px] font-bold rounded-full border inline-flex items-center gap-1 transition-all ${isActive ? `${active} -translate-y-[1px] border-transparent` : `${idle} hover:-translate-y-[1px] hover:shadow-sm`}`}
                  data-testid={`followup-filter-chip-${k}`}
                >
                  {isActive && <span className="w-1 h-1 rounded-full bg-white/95" />}
                  <span className="text-[9px] opacity-90">{icon}</span>
                  {l} <span className="mono opacity-90">({count})</span>
                </button>
              );
            })}
          </div>

          <div className="w-px h-4 bg-slate-200 self-center" />

          {/* Type + Assigned facet dropdowns (moved from search-bar row) */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-slate-500 mr-1">Type</span>
            <FacetDropdown
              label="Type"
              options={typeOptions.map((t) => ({
                value: t,
                label: t === TYPE_NOT_FILLED ? "Not Filled" : t,
              }))}
              selected={typeFilters}
              onToggle={toggleSet(setTypeFilters)}
              onClear={() => setTypeFilters(new Set())}
              testid="crm-filter-type"
            />
          </div>

          <div className="w-px h-4 bg-slate-200 self-center" />

          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-slate-500 mr-1">Assigned</span>
            <FacetDropdown
              label="Assigned"
              options={assignedOptions.map((a) => ({
                value: a,
                label: a === "__UNASSIGNED__" ? "Unassigned" : a,
              }))}
              selected={assignedFilters}
              onToggle={toggleSet(setAssignedFilters)}
              onClear={() => setAssignedFilters(new Set())}
              testid="crm-filter-assigned"
            />
          </div>

          {(typeFilters.size + locationFilters.size + assignedFilters.size + (stageFilter !== "all" ? 1 : 0) + (udiseFilter !== "all" ? 1 : 0) + (contactFilter !== "all" ? 1 : 0) + (stateFilter !== "all" ? 1 : 0) + (districtFilter !== "all" ? 1 : 0) + (followupFilter !== "all" ? 1 : 0)) > 0 && (
            <button
              onClick={clearAllFilters}
              className="h-7 px-2.5 text-[10.5px] font-semibold rounded-full border bg-white border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1 ml-auto"
              data-testid="crm-filter-clear-all"
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk action bar — visible to admins. Selection counter + actions. */}
      {isAdmin && (
        <div className="flex items-center gap-2 flex-wrap rounded-xl border border-sky-200/80 bg-sky-50/40 px-3 py-2 text-[12px]" data-testid="crm-bulk-bar">
          <span className="font-semibold text-slate-700">
            {selected.size > 0
              ? <>Selected <b className="text-sky-700">{selected.size}</b> of {filtered.length}</>
              : <>Bulk actions — select clients to reassign in one go</>}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={selected.size === 0}
            onClick={openBulkSelection}
            className="h-8 text-[11px]"
            data-testid="crm-bulk-reassign-selected"
          >
            Reassign Selected
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={selected.size === 0}
            onClick={() => setBulkStateOpen(true)}
            className="h-8 text-[11px]"
            data-testid="crm-bulk-update-state"
          >
            Bulk Update State
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={selected.size < 2}
            onClick={() => setMergeOpen(true)}
            className="h-8 text-[11px]"
            data-testid="crm-bulk-merge"
          >
            Merge Clients
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={openBulkTransfer}
            className="h-8 text-[11px]"
            data-testid="crm-bulk-transfer-from-user"
          >
            Transfer All from User
          </Button>
          {selected.size > 0 && (
            <button
              onClick={() => setSelected(new Set())}
              className="ml-auto text-[11px] text-slate-500 hover:text-slate-700 underline"
              data-testid="crm-bulk-clear-selection"
            >
              Clear selection
            </button>
          )}
        </div>
      )}

      {crmViewMode === "list" && (
      <div className="relative overflow-hidden rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50/40 via-white to-cyan-50/30 overflow-x-auto">
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-sky-200/30 rounded-full blur-3xl pointer-events-none" />
        <table className="relative w-full text-sm responsive-table">
          <thead className="border-b border-sky-200 bg-sky-50/60">
            <tr>
              {isAdmin && (
                <th className="px-3 py-3 w-10 text-center">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((c) => selected.has(c.id))}
                    onChange={(e) => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) filtered.forEach((c) => next.add(c.id));
                        else filtered.forEach((c) => next.delete(c.id));
                        return next;
                      });
                    }}
                    className="h-4 w-4 cursor-pointer accent-sky-600"
                    data-testid="crm-select-all"
                    aria-label="Select all visible clients"
                  />
                </th>
              )}
              <Th>Client / School</Th><Th>U-Dise</Th><Th>Type</Th><Th>District</Th><Th>State</Th>
              <Th>Contacts</Th><Th>Assigned</Th>
              {filter === "pending" && <Th>Created By</Th>}
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={(isAdmin ? 1 : 0) + (filter === "pending" ? 9 : 8)} className="text-center py-12 text-slate-400">No clients yet.</td></tr>}
            {filtered.map((c) => {
              const contactList = Array.isArray(c.contacts) ? c.contacts : [];
              const hasAnyContact = contactList.some((x) => x && ((x.phone || "").trim() || (x.email || "").trim() || (x.name || "").trim()));
              const noContact = contactList.length === 0 || !hasAnyContact;
              const lastLog = c.last_log;
              const rowColSpan = (isAdmin ? 1 : 0) + (filter === "pending" ? 9 : 8);
              return (
              <React.Fragment key={c.id}>
              {/* v109.5 — Full-width Important Remark banner spans the entire row */}
              {c.important_remark && (
                <tr className="bg-gradient-to-r from-rose-50 via-rose-50/70 to-transparent">
                  <td colSpan={rowColSpan} className="px-4 py-2" data-testid={`crm-row-remark-${c.id}`}>
                    <div className="flex items-center gap-2 text-[11.5px] text-rose-800">
                      <AlertTriangle size={12} className="shrink-0 text-rose-600" />
                      <span className="text-[9.5px] uppercase tracking-wider font-bold text-rose-600 mr-1">Important Remark ·</span>
                      <span className="italic truncate">{c.important_remark}</span>
                    </div>
                  </td>
                </tr>
              )}
              <tr className={`border-b border-sky-100/60 hover:bg-white/60 transition-colors ${selected.has(c.id) ? "bg-sky-50/80" : ""}`} data-testid={`crm-row-${c.u_dise || c.client_name}`} data-highlight-id={c.id}>
                {isAdmin && (
                  <td className="px-3 py-3 text-center align-top" data-label="Select">
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={(e) => {
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(c.id); else next.delete(c.id);
                          return next;
                        });
                      }}
                      className="h-4 w-4 cursor-pointer accent-sky-600"
                      data-testid={`crm-select-${c.id}`}
                      aria-label={`Select ${c.client_name}`}
                    />
                  </td>
                )}
                <td className="px-4 py-3 align-top max-w-[340px]" data-label="Client">
                  <div className="font-bold text-slate-900 truncate flex items-center gap-2 flex-wrap">
                    {c.client_name}
                    {c.doc_lifecycle === "Inactive" && (
                      <Badge className="bg-slate-200 text-slate-700 text-[10px]" data-testid={`crm-inactive-${c.id}`}>
                        Inactive
                      </Badge>
                    )}
                  </div>
                  {/* v109 — Stage sits right under the school name */}
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    <CrmStageBadge
                      client={c}
                      isMasterAdmin={isMaster}
                      currentUserId={user?.id || ""}
                      onCleared={() => {
                        setClients((arr) => arr.map((x) => x.id === c.id
                          ? { ...x, crm_tag: null, crm_tag_by_id: null, crm_tag_by_name: null, crm_tag_at: null, crm_stage: deriveStage({ ...x, crm_tag: null }) }
                          : x));
                      }}
                    />
                    <CrmStageButtons
                      client={c}
                      onChanged={(tag) => {
                        const now = new Date().toISOString();
                        setClients((arr) => arr.map((x) => x.id === c.id
                          ? { ...x, crm_tag: tag, crm_tag_by_id: user?.id, crm_tag_by_name: user?.name || user?.email || "", crm_tag_at: now, crm_stage: tag }
                          : x));
                      }}
                    />
                  </div>
                  {c.client_email && <div className="text-[10.5px] text-slate-500 mono mt-1 truncate">{c.client_email}</div>}
                </td>
                <td className="px-4 py-3 mono text-[12px] align-top" data-label="U-Dise">
                  <div className="flex flex-col gap-1 items-start">
                    <span className={(c.udise_flags || []).includes("Missing") ? "text-slate-400" : "text-slate-700"}>
                      {c.u_dise_display || c.u_dise || "00000000000"}
                    </span>
                    {(c.udise_flags || []).length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap">
                        {(c.udise_flags || []).includes("Invalid") && (
                          <span className="text-[9px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 border border-rose-200 rounded-full px-1.5 py-0.5" data-testid={`udise-flag-invalid-${c.id}`}>Invalid</span>
                        )}
                        {(c.udise_flags || []).includes("Duplicate") && (
                          <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5" data-testid={`udise-flag-duplicate-${c.id}`}>Duplicate</span>
                        )}
                        {(c.udise_flags || []).includes("Missing") && (
                          <span className="text-[9px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 border border-slate-300 rounded-full px-1.5 py-0.5" data-testid={`udise-flag-missing-${c.id}`}>Missing</span>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 align-top" data-label="Type">
                  <Badge className={`${c.client_type?.startsWith("ATL") ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200" : "bg-sky-100 text-sky-700 ring-1 ring-sky-200"} text-[10px] px-1.5 py-0.5`}>{c.client_type || "—"}</Badge>
                </td>
                <td className="px-4 py-3 text-[12px] align-top" data-label="District">{c.district || "—"}</td>
                <td className="px-4 py-3 text-[12px] align-top" data-label="State">{c.state || "—"}</td>
                <td className="px-4 py-3 align-top" data-label="Contacts">
                  {noContact ? (
                    <Badge className="bg-rose-100 text-rose-700 ring-1 ring-rose-200 text-[10px]" data-testid={`crm-nocontact-${c.id}`} data-keep-color>No Contact</Badge>
                  ) : (
                    <span className="mono text-[12px] text-slate-700">{contactList.length}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[12px] align-top" data-label="Assigned">{c.assigned_to_name || "—"}</td>
                {filter === "pending" && (
                  <td className="px-4 py-3 text-[12px] align-top" data-label="Created By">
                    <div className="font-semibold text-slate-800">{c.created_by_name || <span className="italic text-slate-400">—</span>}</div>
                    {c.created_at && (
                      <div className="text-[10px] text-slate-500 mono mt-0.5">{new Date(c.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                    )}
                  </td>
                )}
                <td className="px-4 py-3 text-right align-top" data-label="Actions">
                  <div className="flex items-center justify-end gap-1.5 flex-wrap">
                    {c.approval_status === "Pending" && <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`crm-status-${c.id}`}>Pending</Badge>}
                    {c.approval_status === "Rejected" && <Badge className="bg-red-100 text-red-700 text-[10px]">Rejected</Badge>}
                    {isAdmin && c.approval_status !== "Approved" && (
                      <>
                        <Button size="sm" onClick={() => approveClient(c)} className="h-8 w-8 p-0 bg-green-600 hover:bg-green-700 text-white" title="Approve" data-testid={`crm-approve-${c.id}`}><svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg></Button>
                        <Button size="sm" variant="outline" onClick={() => rejectClient(c)} className="h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50" title="Reject" data-testid={`crm-reject-${c.id}`}><X size={13} /></Button>
                      </>
                    )}
                    {isAdmin && (
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)} className="h-8 w-8 p-0" title="Edit" data-testid={`crm-edit-${c.id}`}><Pencil size={12} /></Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => openLogs(c)} className="h-8 px-2 text-xs border-blue-200 text-blue-700 hover:bg-blue-50" title="View reporting logs" data-testid={`crm-logs-${c.id}`}>
                      <ClipboardList size={12} /> <span className="ml-1 mono font-bold">{c.log_count || 0}</span>
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openHistory(c)} className="h-8 w-8 p-0" title="History" data-testid={`crm-history-${c.id}`}><History size={12} /></Button>
                    {isMaster && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50" title="Delete" data-testid={`crm-delete-${c.id}`}><Trash2 size={12} /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {c.client_name}?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(c.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </td>
              </tr>
              {/* v109.5 — Full-width Last Log strip spans the entire row */}
              {lastLog && (lastLog.at || lastLog.by_name) && (
                <tr className="bg-gradient-to-r from-blue-50/70 via-sky-50/50 to-transparent border-b border-sky-100">
                  <td colSpan={rowColSpan} className="px-4 py-2" data-testid={`crm-row-lastlog-${c.id}`}>
                    <div className="flex items-center gap-3 text-[11px] text-slate-700 flex-wrap">
                      <div className="flex items-center gap-1 text-blue-700">
                        <ClipboardList size={11} />
                        <span className="text-[9.5px] uppercase tracking-wider font-bold">Last Log</span>
                      </div>
                      <span className="font-bold text-slate-800">{lastLog.by_name || "—"}</span>
                      {lastLog.at && <span className="mono text-[10.5px] text-slate-500">{new Date(lastLog.at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
                      {lastLog.connection_method && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700 ring-1 ring-purple-200 rounded-full px-2 py-0.5" data-keep-color>
                          {lastLog.connection_method}
                        </span>
                      )}
                      {lastLog.lead_status && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 rounded-full px-2 py-0.5" data-keep-color>
                          {lastLog.lead_status}
                        </span>
                      )}
                      {lastLog.next_follow_up && (
                        <span className="inline-flex items-center gap-1 text-[10px] mono bg-amber-50 text-amber-800 ring-1 ring-amber-200 rounded-full px-2 py-0.5" data-keep-color>
                          Next: {lastLog.next_follow_up}
                        </span>
                      )}
                      {lastLog.note && (
                        <span className="text-[11px] text-slate-600 italic flex-1 min-w-[240px] truncate">&ldquo;{lastLog.note}&rdquo;</span>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {crmViewMode === "card" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="crm-cards">
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-400 text-sm">No clients match your filters.</div>
          )}
          {filtered.map((c) => {
            const contactList = Array.isArray(c.contacts) ? c.contacts : [];
            const hasAnyContact = contactList.some((x) => x && ((x.phone || "").trim() || (x.email || "").trim() || (x.name || "").trim()));
            const noContact = contactList.length === 0 || !hasAnyContact;
            const lastLog = c.last_log;
            return (
              <div key={c.id} className={`rounded-xl border border-sky-200 bg-white p-4 flex flex-col gap-2 transition-colors hover:border-sky-400 ${selected.has(c.id) ? "ring-2 ring-sky-400" : ""}`} data-testid={`crm-card-${c.u_dise || c.client_name}`}>
                {/* Important Remark → TOP (full text, prettier box) */}
                {c.important_remark && (
                  <div className="rounded-lg bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 px-3 py-2 text-[11px] text-rose-800 flex items-start gap-2" data-testid={`crm-card-remark-${c.id}`}>
                    <AlertTriangle size={12} className="mt-0.5 shrink-0 text-rose-600" />
                    <div className="italic leading-snug whitespace-pre-wrap break-words">{c.important_remark}</div>
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    {isAdmin && (
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={(e) => { setSelected((prev) => { const next = new Set(prev); if (e.target.checked) next.add(c.id); else next.delete(c.id); return next; }); }}
                        className="h-3.5 w-3.5 cursor-pointer accent-sky-600 mr-2 align-middle"
                        data-testid={`crm-card-select-${c.id}`}
                      />
                    )}
                    <span className="font-bold text-slate-900 truncate">{c.client_name}</span>
                    {/* Stage → immediately below school name */}
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      <CrmStageBadge
                        client={c}
                        isMasterAdmin={isMaster}
                        currentUserId={user?.id || ""}
                        onCleared={() => {
                          setClients((arr) => arr.map((x) => x.id === c.id
                            ? { ...x, crm_tag: null, crm_tag_by_id: null, crm_tag_by_name: null, crm_tag_at: null, crm_stage: deriveStage({ ...x, crm_tag: null }) }
                            : x));
                        }}
                      />
                      <Badge className={`${c.client_type?.startsWith("ATL") ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200" : "bg-sky-100 text-sky-700 ring-1 ring-sky-200"} text-[9.5px] px-1.5 py-0.5`}>{c.client_type || "—"}</Badge>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {c.approval_status === "Pending" && <Badge className="bg-amber-100 text-amber-800 text-[9.5px]" data-keep-color>Pending</Badge>}
                    {c.approval_status === "Rejected" && <Badge className="bg-red-100 text-red-700 text-[9.5px]" data-keep-color>Rejected</Badge>}
                    {noContact && <Badge className="bg-rose-100 text-rose-700 ring-1 ring-rose-200 text-[9.5px]" data-testid={`crm-card-nocontact-${c.id}`} data-keep-color>No Contact</Badge>}
                  </div>
                </div>
                <div className="border-t border-slate-100 pt-2 grid grid-cols-2 gap-1.5 text-[11.5px]">
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">District</div>
                    <div className="text-slate-800 truncate">{c.district || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">State</div>
                    <div className="text-slate-800 truncate">{c.state || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">U-DISE</div>
                    <div className="mono text-slate-800">{c.u_dise_display || c.u_dise || "00000000000"}</div>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Assigned</div>
                    <div className="text-slate-800 truncate">{c.assigned_to_name || "—"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap pt-1.5 border-t border-slate-100">
                  <CrmStageButtons
                    client={c}
                    onChanged={(tag) => {
                      const now = new Date().toISOString();
                      setClients((arr) => arr.map((x) => x.id === c.id
                        ? { ...x, crm_tag: tag, crm_tag_by_id: user?.id, crm_tag_by_name: user?.name || user?.email || "", crm_tag_at: now, crm_stage: tag }
                        : x));
                    }}
                  />
                  <div className="flex-1" />
                  {isAdmin && c.approval_status !== "Approved" && (
                    <>
                      <Button size="sm" onClick={() => approveClient(c)} className="h-8 w-8 p-0 bg-green-600 hover:bg-green-700 text-white" title="Approve" data-testid={`crm-card-approve-${c.id}`}><svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg></Button>
                      <Button size="sm" variant="outline" onClick={() => rejectClient(c)} className="h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50" title="Reject" data-testid={`crm-card-reject-${c.id}`}><X size={13} /></Button>
                    </>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => openEdit(c)} className="h-8 w-8 p-0" title="Edit" data-testid={`crm-card-edit-${c.id}`}><Pencil size={12} /></Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openLogs(c)} className="h-8 px-2 text-xs border-blue-200 text-blue-700 hover:bg-blue-50" title="Reporting logs" data-testid={`crm-card-logs-${c.id}`}>
                    <ClipboardList size={12} /> <span className="ml-1 mono font-bold">{c.log_count || 0}</span>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openHistory(c)} className="h-8 w-8 p-0" title="History" data-testid={`crm-card-history-${c.id}`}><History size={12} /></Button>
                  {isMaster && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50" title="Delete" data-testid={`crm-card-delete-${c.id}`}><Trash2 size={12} /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {c.client_name}?</AlertDialogTitle>
                          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(c.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
                {/* Last Log → BOTTOM of the school card (full detail box) */}
                {lastLog && (lastLog.at || lastLog.by_name) && (
                  <div className="mt-1 rounded-lg bg-blue-50/70 border border-blue-200 px-3 py-2 text-[11px] text-slate-700" data-testid={`crm-card-lastlog-${c.id}`}>
                    <div className="flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-wider text-blue-700 mb-1 flex-wrap">
                      <ClipboardList size={10} /> Last log
                      {lastLog.at && <span className="mono text-blue-600 font-normal">· {new Date(lastLog.at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
                    </div>
                    <div className="font-bold text-slate-800 text-[11.5px]">{lastLog.by_name || "—"}</div>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {lastLog.connection_method && (
                        <span className="text-[9.5px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700 ring-1 ring-purple-200 rounded-full px-1.5 py-0.5" data-keep-color>
                          {lastLog.connection_method}
                        </span>
                      )}
                      {lastLog.lead_status && (
                        <span className="text-[9.5px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 rounded-full px-1.5 py-0.5" data-keep-color>
                          {lastLog.lead_status}
                        </span>
                      )}
                      {lastLog.next_follow_up && (
                        <span className="text-[9.5px] mono bg-amber-50 text-amber-800 ring-1 ring-amber-200 rounded-full px-1.5 py-0.5" data-keep-color>
                          Next: {lastLog.next_follow_up}
                        </span>
                      )}
                    </div>
                    {lastLog.note && <div className="text-[10.5px] text-slate-600 mt-1 whitespace-pre-wrap break-words leading-snug italic">&ldquo;{lastLog.note}&rdquo;</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bulk Assign Dialog — supports two modes */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md" data-testid="crm-bulk-dialog">
          <DialogHeader>
            <DialogTitle>
              {bulkMode === "transfer" ? "Transfer All Clients from a User" : `Reassign ${selected.size} Selected Client(s)`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[12.5px]">
            {bulkMode === "transfer" && (
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Current Owner (transfer FROM)</label>
                <select
                  value={bulkFromUserId}
                  onChange={(e) => setBulkFromUserId(e.target.value)}
                  className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-400"
                  data-testid="crm-bulk-from-user"
                >
                  <option value="">— Select current owner —</option>
                  {bulkUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.role}{u.employee_code ? ` · ${u.employee_code}` : ""})
                    </option>
                  ))}
                </select>
                <div className="text-[11px] text-slate-500 mt-1">
                  All clients currently assigned to this user will be reassigned to the new owner.
                </div>
              </div>
            )}
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                New Owner (assign TO)
              </label>
              <select
                value={bulkTargetId}
                onChange={(e) => setBulkTargetId(e.target.value)}
                className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-400"
                data-testid="crm-bulk-target-user"
              >
                <option value="">— Select new owner —</option>
                {bulkUsers.map((u) => (
                  <option key={u.id} value={u.id} disabled={u.id === bulkFromUserId}>
                    {u.name} ({u.role}{u.employee_code ? ` · ${u.employee_code}` : ""})
                  </option>
                ))}
              </select>
            </div>
            {bulkMode === "selection" && (
              <div className="rounded-md bg-sky-50 border border-sky-100 px-3 py-2 text-[11.5px] text-sky-900">
                <b>{selected.size}</b> client(s) selected. Their Sales Owner will be set to the user picked above.
              </div>
            )}
            <div className="text-[10.5px] text-slate-500 italic">
              Every reassignment is added to the client history log with your name as the actor.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button
              onClick={submitBulkAssign}
              disabled={bulkBusy || !bulkTargetId || (bulkMode === "transfer" && !bulkFromUserId)}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="crm-bulk-submit"
            >
              {bulkBusy ? "Reassigning…" : "Reassign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v109.9.168 — Bulk Update State */}
      <Dialog open={bulkStateOpen} onOpenChange={setBulkStateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Bulk Update State — {selected.size} client(s)</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">New State</label>
            <Input value={bulkState} onChange={(e) => setBulkState(e.target.value)} placeholder="e.g. Maharashtra" data-testid="crm-bulk-state-input" list="crm-state-options" />
            <datalist id="crm-state-options">
              {locationOptions.map((s) => s !== LOCATION_NOT_FILLED && <option key={s} value={s} />)}
            </datalist>
            <div className="text-[10.5px] text-slate-500 italic">Every selected client&apos;s `state` will be overwritten to the value above. Master Admin / CRM Manager / Sales Admin only.</div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkStateOpen(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!bulkState.trim()) { toast.error("Enter a state"); return; }
                setBulkStateBusy(true);
                try {
                  const { data } = await api.post("/crm/clients/bulk-update-state", { client_ids: Array.from(selected), state: bulkState.trim() });
                  toast.success(`Updated ${data.updated} client(s) → ${data.state}`);
                  setBulkStateOpen(false); setSelected(new Set()); setBulkState(""); load();
                } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                finally { setBulkStateBusy(false); }
              }}
              disabled={bulkStateBusy || !bulkState.trim()}
              className="!bg-sky-600 hover:!bg-sky-700 !text-white"
              data-keep-color
              data-testid="crm-bulk-state-submit"
            >
              {bulkStateBusy ? "Updating…" : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v109.9.168 — Merge Clients */}
      <Dialog open={mergeOpen} onOpenChange={(o) => { if (!o) { setMergeOpen(false); setMergeMainId(""); setMergeFieldMap({}); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Merge {selected.size} Clients</DialogTitle></DialogHeader>
          <MergeDialogBody
            selectedIds={Array.from(selected)}
            clients={clients}
            mainId={mergeMainId}
            setMainId={setMergeMainId}
            fieldMap={mergeFieldMap}
            setFieldMap={setMergeFieldMap}
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMergeOpen(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!mergeMainId) { toast.error("Pick the primary client to merge INTO"); return; }
                if (!window.confirm(`Merge ${selected.size - 1} client(s) into the chosen main? Contacts + reports + trainings + travel from all sources will be re-parented. This cannot be easily undone.`)) return;
                setMergeBusy(true);
                try {
                  const others = Array.from(selected).filter((x) => x !== mergeMainId);
                  const { data } = await api.post("/crm/clients/merge", { main_id: mergeMainId, other_ids: others, field_map: mergeFieldMap });
                  toast.success(`Merged ${data.merged} client(s) — ${data.contacts_total} contacts now on main`);
                  setMergeOpen(false); setMergeMainId(""); setMergeFieldMap({}); setSelected(new Set()); load();
                } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                finally { setMergeBusy(false); }
              }}
              disabled={mergeBusy || !mergeMainId || selected.size < 2}
              className="!bg-fuchsia-600 hover:!bg-fuchsia-700 !text-white"
              data-keep-color
              data-testid="crm-merge-submit"
            >
              {mergeBusy ? "Merging…" : "Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Logs Dialog — shows CRM Reporting log history for a client */}
      <Dialog open={!!logsClient} onOpenChange={(o) => { if (!o) { setLogsClient(null); setLogsList([]); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="crm-logs-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <ClipboardList size={18} className="text-blue-600" />
              CRM Reporting Logs — {logsClient?.client_name}
            </DialogTitle>
          </DialogHeader>
          <div className="text-[11.5px] text-slate-500 mb-2">
            U-DISE <span className="mono">{logsClient?.u_dise || "—"}</span> · {logsList.length} log{logsList.length === 1 ? "" : "s"} on record
          </div>
          {logsLoading ? (
            <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
          ) : logsList.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400 italic">
              No log reports have been filed for this client yet.
            </div>
          ) : (
            <div className="space-y-2.5">
              {logsList.map((r) => (
                <div key={r.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`crm-logs-row-${r.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]">{r.lead_status || "—"}</Badge>
                      <span className="text-[11px] text-slate-500">via {r.connection_method || "—"}</span>
                      {r.connected_contact_name && (
                        <span className="text-[10.5px] text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                          with {r.connected_contact_name}
                        </span>
                      )}
                    </div>
                    <span className="mono text-[10.5px] text-slate-500">
                      {r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </span>
                  </div>
                  <div className="text-[13px] text-slate-800 mt-1.5 whitespace-pre-wrap">{r.remark || <em className="text-slate-400">(no remark)</em>}</div>
                  <div className="flex items-center justify-between gap-2 mt-1.5 flex-wrap">
                    <div className="text-[10.5px] text-slate-500">
                      Filed by <b className="text-slate-700">{r.created_by_name || "—"}</b>
                    </div>
                    {r.next_follow_up && (
                      <div className="text-[10.5px] mono text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                        Next follow-up: {r.next_follow_up}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setLogsClient(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v109.4 — Recycle Bin Dialog */}
      <Dialog open={exportOpen} onOpenChange={(o) => { if (!o) setExportOpen(false); }}>
        <DialogContent className="max-w-sm" data-testid="crm-export-dialog">
          <DialogHeader>
            <DialogTitle>Export Clients</DialogTitle>
            <DialogDescription>
              Choose which type of clients to export. The CSV includes contact
              details (up to 3 contacts per client) automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Client Type</Label>
            <Select value={exportType} onValueChange={setExportType}>
              <SelectTrigger data-testid="crm-export-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {exportTypes.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`crm-export-type-${t.replace(/\W+/g, "-").toLowerCase()}`}>
                    {t === "all" ? "All Client Types" : t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)}>Cancel</Button>
            <Button onClick={exportCSV} disabled={exportBusy} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="crm-export-confirm">
              {exportBusy ? "Exporting…" : "Download CSV"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={recycleOpen} onOpenChange={(o) => { if (!o) setRecycleOpen(false); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="crm-recycle-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <Trash2 size={18} className="text-slate-700" />
              Client Recycle Bin
              <Badge className="bg-slate-100 text-slate-700 text-[10px] ml-1" data-keep-color>{recycleRows.length}</Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="text-[11.5px] text-slate-500 mb-2">
            Rejected and deleted clients live here. <b>Restore</b> brings a client back as Pending. <b>Delete permanently</b> is only available to Master Admin.
          </div>
          {recycleLoading ? (
            <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
          ) : recycleRows.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400 italic">The bin is empty. Rejected or deleted clients will appear here.</div>
          ) : (
            <div className="space-y-2">
              {recycleRows.map((r) => (
                <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-start gap-3" data-testid={`crm-recycle-row-${r.id}`}>
                  <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                    <Building2 size={16} className="text-slate-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-bold text-slate-900 truncate">{r.client_name}</div>
                      {r.deleted_reason === "rejected"
                        ? <Badge className="bg-red-100 text-red-700 ring-1 ring-red-200 text-[10px]" data-keep-color>Rejected</Badge>
                        : <Badge className="bg-slate-200 text-slate-700 text-[10px]" data-keep-color>Deleted</Badge>}
                    </div>
                    <div className="mono text-[11px] text-slate-500 mt-0.5">
                      U-DISE {r.u_dise || "—"} · {[r.district, r.state].filter(Boolean).join(" · ") || "—"}
                    </div>
                    <div className="text-[10.5px] text-slate-500 mt-1">
                      {r.deleted_reason === "rejected" ? "Rejected" : "Deleted"} by <b className="text-slate-700">{r.deleted_by_name || "—"}</b>
                      {r.deleted_at ? ` on ${new Date(r.deleted_at).toLocaleString("en-IN")}` : ""}
                    </div>
                    {r.rejection_reason && (
                      <div className="text-[10.5px] text-rose-700 mt-1 italic">Reason: {r.rejection_reason}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button size="sm" onClick={() => restoreClient(r.id, r.client_name)} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`crm-recycle-restore-${r.id}`}>
                      Restore
                    </Button>
                    {isMaster && (
                      <Button size="sm" variant="outline" onClick={() => purgeClient(r.id, r.client_name)} className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`crm-recycle-purge-${r.id}`}>
                        <Trash2 size={12} className="mr-1" /> Delete permanently
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecycleOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v109.5 — Module Access Dialog (Master Admin only) */}
      <Dialog open={accessOpen} onOpenChange={(o) => { if (!o) setAccessOpen(false); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="crm-module-access-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <Users size={18} className="text-indigo-700" />
              CRM Clients · Module Access
              <Badge className="bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 text-[10px] ml-1" data-keep-color>
                {accessData.access_user_ids.length} granted
              </Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-md border border-indigo-100 bg-indigo-50/50 px-3 py-2 text-[11.5px] text-indigo-800 mb-3">
            Granting a user access here <b>overrides all previous role rules</b> for the CRM Clients module.
            They can view, edit and soft-delete clients. Hard-delete from the Recycle Bin remains <b>Master Admin only</b>.
            Interconnections with Training Requests, Reporting and other modules stay intact.
          </div>
          <div className="space-y-1.5" data-testid="crm-access-list">
            {(accessData.all_employees || []).length === 0 && (
              <div className="py-6 text-center text-sm text-slate-400 italic">Loading employees…</div>
            )}
            {(accessData.all_employees || []).map((e) => {
              const has = accessData.access_user_ids.includes(e.id);
              return (
                <div key={e.id} className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border transition-colors ${has ? "bg-indigo-50/60 border-indigo-200" : "bg-white border-slate-200 hover:bg-slate-50"}`} data-testid={`crm-access-row-${e.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 text-[12.5px] truncate">{e.name}</div>
                    <div className="text-[10.5px] text-slate-500 mono truncate">{e.email} · {e.role}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleAccessUser(e.id)}
                    disabled={accessSaving}
                    className={`h-8 px-3 text-[11px] font-bold rounded-full ring-1 transition-all ${has ? "bg-gradient-to-br from-indigo-600 to-blue-600 text-white ring-indigo-500 shadow" : "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50"}`}
                    data-testid={`crm-access-toggle-${e.id}`}
                    data-keep-color
                  >
                    {has ? "✓ Access granted" : "Grant Access"}
                  </button>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* v109.4 — Compact dropdown picker used for State / District filters. */
function StateDistrictPicker({ label, value, options, onChange, disabled = false, testid, activeCls = "bg-gradient-to-br from-indigo-500 to-blue-600" }) {
  const isActive = value !== "all";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          disabled={disabled}
          className={`h-7 px-2.5 text-[10.5px] font-bold rounded-full border inline-flex items-center gap-1 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
            isActive
              ? `${activeCls} text-white shadow-sm ring-1 ring-white/40 -translate-y-[1px] border-transparent`
              : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 hover:-translate-y-[1px] hover:shadow-sm"
          }`}
          data-testid={testid}
        >
          {isActive && <span className="w-1 h-1 rounded-full bg-white/90" />}
          {label}: <span className="font-normal opacity-90 truncate max-w-[80px]">{value === "all" ? "All" : value}</span>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-72 overflow-y-auto min-w-[200px]" data-testid={`${testid}-menu`}>
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onChange("all")} data-testid={`${testid}-opt-all`}>
          <span className={value === "all" ? "font-bold text-slate-900" : ""}>All {label.toLowerCase()}s</span>
        </DropdownMenuItem>
        {options.map((o) => (
          <DropdownMenuItem key={o} onClick={() => onChange(o)} data-testid={`${testid}-opt-${o}`}>
            <span className={value === o ? "font-bold text-slate-900" : ""}>{o}</span>
          </DropdownMenuItem>
        ))}
        {options.length === 0 && (
          <div className="px-2 py-2 text-[11px] text-slate-400 italic">
            {disabled ? "Pick a State first" : "No options available"}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left text-[10px] uppercase tracking-[0.16em] font-bold text-sky-700 ${className}`}>{children}</th>;
}
function FilterChip({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`h-9 px-3.5 text-[11.5px] font-bold rounded-full border transition-all inline-flex items-center gap-1.5 ${
        active
          ? "bg-gradient-to-br from-sky-500 to-cyan-600 border-transparent text-white shadow-md shadow-sky-500/30 ring-1 ring-sky-400 -translate-y-[1px]"
          : "bg-white border-sky-200 text-sky-700 hover:bg-sky-50 hover:border-sky-300 hover:-translate-y-[1px] hover:shadow-sm"
      }`}
      data-testid={testid}
    >
      {active && <span className="w-1.5 h-1.5 rounded-full bg-white/90" />}
      {label}
    </button>
  );
}

/* v109.6 — Compact single-line Stats Overview: totals + SA leaderboard on ONE row */
function StatsOverview({ analytics, monthLeaders }) {
  const { totals } = analytics;
  const ml = monthLeaders || {};
  const { top_clients, top_contacted, top_positive, top_logs } = ml;
  const mLbl = ml.month_label ? ml.month_label.split(" ")[0] : "Month";
  const tiles = [
    { key: "all",       label: "Total",     value: totals.all,        icon: Users,        tone: "sky" },
    { key: "contacted", label: "Contacted", value: totals.contacted,  icon: PhoneCall,    tone: "emerald" },
    { key: "non",       label: "No Contact",value: totals.non,        icon: PhoneOff,     tone: "rose" },
    { key: "positive",  label: "Positive",  value: totals.positive,   icon: TrendingUp,   tone: "green" },
    { key: "negative",  label: "Negative",  value: totals.negative,   icon: TrendingDown, tone: "orange" },
    { key: "top_clients",   sa: top_clients,   metric: "clients",   label: `${mLbl} · Top Clients`,    icon: Crown,   tone: "violet" },
    { key: "top_contacted", sa: top_contacted, metric: "contacted", label: `${mLbl} · Top Contacted`,  icon: Trophy,  tone: "amber" },
    { key: "top_positive",  sa: top_positive,  metric: "positive",  label: `${mLbl} · Top Positive`,   icon: Award,   tone: "emerald" },
    { key: "top_logs",      sa: top_logs,      metric: "logs",      label: `${mLbl} · Top Logs`,       icon: MessageSquare, tone: "blue" },
  ];
  const toneMap = {
    sky:     { bg: "bg-gradient-to-br from-sky-50 to-cyan-50",         ring: "ring-sky-200",     icon: "text-sky-600",     accent: "text-sky-800" },
    emerald: { bg: "bg-gradient-to-br from-emerald-50 to-green-50",    ring: "ring-emerald-200", icon: "text-emerald-600", accent: "text-emerald-800" },
    rose:    { bg: "bg-gradient-to-br from-rose-50 to-pink-50",        ring: "ring-rose-200",    icon: "text-rose-600",    accent: "text-rose-800" },
    green:   { bg: "bg-gradient-to-br from-green-50 to-emerald-50",    ring: "ring-green-200",   icon: "text-green-600",   accent: "text-green-800" },
    orange:  { bg: "bg-gradient-to-br from-orange-50 to-amber-50",     ring: "ring-orange-200",  icon: "text-orange-600",  accent: "text-orange-800" },
    violet:  { bg: "bg-gradient-to-br from-violet-50 to-purple-50",    ring: "ring-violet-200",  icon: "text-violet-600",  accent: "text-violet-800" },
    amber:   { bg: "bg-gradient-to-br from-amber-50 to-yellow-50",     ring: "ring-amber-200",   icon: "text-amber-600",   accent: "text-amber-800" },
    blue:    { bg: "bg-gradient-to-br from-blue-50 to-sky-50",         ring: "ring-blue-200",    icon: "text-blue-600",    accent: "text-blue-800" },
  };
  return (
    <div className="grid grid-cols-5 lg:grid-cols-9 gap-1.5" data-testid="crm-stats-overview">
      {tiles.map((t) => {
        const s = toneMap[t.tone];
        const Icon = t.icon;
        const isSA = !!t.metric;
        const val = isSA ? (t.sa ? t.sa[t.metric] : 0) : t.value;
        const saName = isSA ? (t.sa ? t.sa.name : "—") : null;
        return (
          <div
            key={t.key}
            className={`rounded-lg border ring-1 ${s.ring} ${s.bg} px-2 py-1.5 flex items-center gap-1.5 transition-transform hover:-translate-y-[1px] min-w-0`}
            data-testid={`crm-stat-${t.key}`}
            title={isSA && t.sa ? `${t.sa.name} · ${val} ${t.metric}` : `${t.label}: ${val}`}
          >
            <div className={`w-6 h-6 rounded bg-white/70 flex items-center justify-center shrink-0 ${s.icon}`}>
              <Icon size={12} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[8.5px] uppercase tracking-wide font-bold text-slate-500 truncate leading-none">{t.label}</div>
              {isSA ? (
                <>
                  <div className={`text-[10px] font-bold ${s.accent} truncate leading-tight`}>{saName || "—"}</div>
                  <div className="text-[9px] mono text-slate-500 leading-none">{val}</div>
                </>
              ) : (
                <div className={`text-[14px] mono font-bold leading-tight ${s.accent}`}>{val}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FacetDropdown({ label, options, selected, onToggle, onClear, testid }) {
  const count = selected.size;
  const active = count > 0;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`h-7 px-2.5 text-[10.5px] font-bold rounded-full border transition-all inline-flex items-center gap-1 ${
            active
              ? "bg-gradient-to-br from-sky-500 to-cyan-600 border-sky-500 text-white shadow-sm -translate-y-[1px]"
              : "bg-white border-sky-200 text-sky-700 hover:bg-sky-50 hover:-translate-y-[1px] hover:shadow-sm"
          }`}
          data-testid={testid}
        >
          <FilterIcon size={11} />
          {label}
          {active && (
            <span className="ml-0.5 inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 text-[9px] font-bold rounded-full bg-white/25 text-white">
              {count}
            </span>
          )}
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 max-h-80 overflow-y-auto">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center justify-between">
          <span>{label}</span>
          {count > 0 && (
            <button
              onClick={(e) => { e.preventDefault(); onClear(); }}
              className="text-[10px] font-semibold text-rose-600 hover:underline"
              data-testid={`${testid}-clear`}
            >
              Clear
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.length === 0 && (
          <div className="px-2 py-3 text-[12px] text-slate-400 italic text-center">No options yet</div>
        )}
        {options.map((opt) => (
          <DropdownMenuCheckboxItem
            key={opt.value}
            checked={selected.has(opt.value)}
            onCheckedChange={() => onToggle(opt.value)}
            onSelect={(e) => e.preventDefault()}
            className="text-[12px] cursor-pointer"
            data-testid={`${testid}-opt-${String(opt.value).replace(/[^A-Za-z0-9]/g, "_")}`}
          >
            {opt.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// v109.9.168 — Merge dialog body. Renders one row per candidate field and
// lets Master Admin / CRM admins pick which client's value wins.
function MergeDialogBody({ selectedIds, clients, mainId, setMainId, fieldMap, setFieldMap }) {
  const rows = clients.filter((c) => selectedIds.includes(c.id));
  const FIELDS = [
    ["client_name", "Client Name"],
    ["u_dise", "U-DISE"],
    ["client_types", "School Types"],
    ["client_type", "Legacy Type"],
    ["state", "State"],
    ["district", "District"],
    ["city", "City"],
    ["pincode", "Pincode"],
    ["address", "Address"],
    ["gstin", "GSTIN"],
    ["assigned_to_id", "Sales Associate"],
    ["remark", "Remark"],
  ];
  const val = (c, k) => {
    const v = c?.[k];
    if (Array.isArray(v)) return v.join(", ");
    return v == null ? "" : String(v);
  };
  return (
    <div className="space-y-4 py-2">
      <div>
        <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-1">1. Pick the primary client (data will merge INTO this one)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {rows.map((c) => (
            <label key={c.id} className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer ${mainId === c.id ? "border-fuchsia-500 bg-fuchsia-50" : "border-slate-200 hover:bg-slate-50"}`}>
              <input type="radio" name="mergeMain" checked={mainId === c.id} onChange={() => setMainId(c.id)} className="mt-0.5" data-testid={`crm-merge-main-${c.id}`} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[12.5px] text-slate-900 truncate">{c.client_name}</div>
                <div className="text-[10.5px] text-slate-500 truncate">{[c.u_dise, c.city, c.state].filter(Boolean).join(" · ")}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-1">2. Per-field winner (leave blank to keep the main&apos;s value)</div>
        <div className="rounded-md border border-slate-200 overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left [&>th]:text-[10px] [&>th]:uppercase [&>th]:text-slate-600">
                <th>Field</th>
                {rows.map((c) => (
                  <th key={c.id} className={mainId === c.id ? "text-fuchsia-700" : ""}>{c.client_name}{mainId === c.id ? " · MAIN" : ""}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FIELDS.map(([k, label]) => (
                <tr key={k} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-semibold text-slate-700">{label}</td>
                  {rows.map((c) => {
                    const cur = val(c, k);
                    const winner = fieldMap[k];
                    const isWinner = winner === cur && cur !== "";
                    return (
                      <td key={c.id} className="px-3 py-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            if (!cur) return;
                            const next = { ...fieldMap };
                            if (isWinner) delete next[k]; else next[k] = c[k];
                            setFieldMap(next);
                          }}
                          className={`w-full text-left px-2 py-1 rounded text-[11.5px] truncate ${isWinner ? "bg-fuchsia-600 text-white" : cur ? "bg-slate-100 hover:bg-slate-200" : "text-slate-300"}`}
                          title={cur || "(blank)"}
                          disabled={!cur}
                          data-testid={`crm-merge-field-${k}-${c.id}`}
                        >
                          {cur || "—"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[10.5px] text-slate-500 italic mt-2">Contacts, CRM reports, training requests, and travel records referencing the other clients will all be re-parented to the main. The other clients are soft-deleted with a `merged_into` breadcrumb.</div>
      </div>
    </div>
  );
}
